import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type * as React from "react";
import type { ReactNode } from "react";
import { cn } from "@/lib/clientUtils";

function TooltipProvider({
  delayDuration = 500,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        className={cn(
          "fade-in-0 zoom-in-95 data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit animate-in text-balance rounded-md border bg-background px-3 py-1.5 text-foreground text-xs shadow data-[state=closed]:animate-out",
          className,
        )}
        data-slot="tooltip-content"
        sideOffset={sideOffset + 4}
        {...props}
      >
        {children}
        {/* <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]  bg-background fill-background border hidden" /> */}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

interface BasicTooltipProps
  extends React.ComponentProps<typeof TooltipPrimitive.Root> {
  children: ReactNode;
  tooltip: ReactNode;
  disabled?: boolean;
  asChild?: boolean;
  className?: string;
}

function BasicTooltip({
  children,
  tooltip,
  disabled,
  asChild = true,
  className,
  delayDuration,
  ...props
}: BasicTooltipProps) {
  return (
    <Tooltip {...props} delayDuration={delayDuration ?? 100}>
      <TooltipTrigger
        disabled={disabled}
        asChild={asChild}
        className={className}
      >
        {children}
      </TooltipTrigger>

      {!disabled && tooltip && <TooltipContent>{tooltip}</TooltipContent>}
    </Tooltip>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, BasicTooltip };
